import { useLazyQuery } from '@apollo/client';
import { GetBaseUserResponse, LoginUserResponse } from '@/api/types/user.types';
import { GET_BASE_USER } from '@/api/queries/user.queries';
import { AuthStatus, useAuthStore } from '@/store/useAuthStore';
import { useSafeMutation } from '@/api/hooks/useSafeMutation';
import { LOGIN_USER } from '@/api/mutations/users.mutations';

export function useAuth() {
  const [setIsAuthLoaded, setUserId, setAuthStatus, setIsAuthenticated] =
    useAuthStore(state => [
      state.setIsAuthLoaded,
      state.setUserId,
      state.setAuthStatus,
      state.setIsAuthenticated,
    ]);

  const [getBaseUser] = useLazyQuery<GetBaseUserResponse>(GET_BASE_USER, {
    fetchPolicy: 'network-only',
    onCompleted: data => handleId(data?.currentUser?.id),
    onError: handleError,
  });

  const [loginUser] = useSafeMutation<LoginUserResponse>(LOGIN_USER, {
    onCompleted: data => handleId(data?.loginUser?.entity),
    onError: handleError,
  });

  function handleAnonymous() {
    setIsAuthenticated(false);
    setAuthStatus(AuthStatus.Unauthenticated);
  }

  function handleAuthenticated(id: string) {
    setUserId(id);
    setIsAuthenticated(true);
    setAuthStatus(AuthStatus.Authenticated);
  }

  function handleError() {
    setIsAuthLoaded(true);
    setUserId(null);
    setIsAuthenticated(false);
    setAuthStatus(AuthStatus.Unauthenticated);
  }

  function handleId(id: string) {
    setIsAuthLoaded(true);
    setUserId(null);

    if (id) {
      handleAuthenticated(id);
    } else {
      handleAnonymous();
    }
  }

  return { getBaseUser, loginUser };
}
